<template>
  <div class="product-report">
    <v-card flat outlined class="report-table-card">
      <v-card-title class="report-table-header">
        <div>
          <div class="report-title">Product Sales</div>
          <div class="report-subtitle">
            {{ Product.length }} products found
          </div>
        </div>

        <v-spacer />

        <div class="summary-box">
          <div class="summary-label">Qty</div>
          <div class="summary-value">{{ totalQty }}</div>
        </div>

        <div class="summary-box">
          <div class="summary-label">Sales</div>
          <div class="summary-value">{{ totalPrice | currency }}</div>
        </div>

        <div v-if="!hideCost" class="summary-box">
          <div class="summary-label">Cost</div>
          <div class="summary-value">{{ totalCost | currency }}</div>
        </div>

        <div v-if="!hideProfit" class="summary-box">
          <div class="summary-label">Profit</div>
          <div class="summary-value" :class="{ loss: totalProfit < 0 }">
            {{ totalProfit | currency }}
          </div>
        </div>
      </v-card-title>

      <v-divider />

      <v-card-text class="pa-0">
        <v-progress-linear v-if="isLoading" indeterminate height="3" />

        <v-data-table
          v-else
          dense
          readonly
          :sort-by="sortBy"
          sort-desc
          :mobile-breakpoint="0"
          :headers="visibleHeaders"
          :items="Product"
          :items-per-page="-1"
          :expanded.sync="expanded"
          show-expand
          hide-default-footer
          class="product-table"
        >
          <template v-slot:item.data-table-expand="{ item, isExpanded, expand }">
            <v-btn class="no-print" icon small @click="expand(!isExpanded)">
              <v-icon v-if="isExpanded">mdi-chevron-up</v-icon>
              <v-icon v-else>mdi-chevron-down</v-icon>
            </v-btn>
          </template>

          <template v-slot:item.name="{ item }">
            <v-icon v-if="item.trackSerialNumber" small color="blue">
              mdi-barcode-scan
            </v-icon>

            <v-icon v-else small color="teal">
              mdi-package-variant-closed
            </v-icon>

            <span class="item-name ml-2">
              {{ item.name || "N/A" }}
            </span>
          </template>

          <template v-slot:item.totalQty="{ item }">
            <span class="font-weight-bold">
              {{ item.totalQty }}
            </span>

            <span class="percent-text">
              {{ percentQty(item.totalQty) }}%
            </span>
          </template>

          <template v-slot:item.totalPrice="{ item }">
            <span class="money">
              {{ item.totalPrice | currency }}
            </span>
          </template>

          <template v-slot:item.totalCost="{ item }">
            <span class="money">
              {{ item.totalCost | currency }}
            </span>
          </template>

          <template v-slot:item.totalProfit="{ item }">
            <span class="money" :class="{ loss: getProfit(item) < 0 }">
              {{ getProfit(item) | currency }}
            </span>

            <span class="profit-percent">
              {{ profitPercent(item) }}%
            </span>
          </template>

          <template v-slot:expanded-item="{ headers, item }">
            <td :colspan="headers.length" class="pa-0">
              <div class="expanded-panel">
                <InvoiceDetail
                  :ProductId="item.id"
                  :CustomerId="CustomerId"
                  :start="start"
                  :end="end"
                  :hideProfit="hideProfit"
                  :hideCost="hideCost"
                />
              </div>
            </td>
          </template>

          <template v-slot:body.append v-if="Product.length > 0">
            <tr class="total-row">
              <td></td>
              <td class="font-weight-bold">Total</td>

              <td class="text-center font-weight-bold">
                {{ totalQty }}
                <span class="percent-text">100%</span>
              </td>

              <td class="text-right font-weight-bold">
                {{ totalPrice | currency }}
              </td>

              <td v-if="!hideCost" class="text-right font-weight-bold">
                {{ totalCost | currency }}
              </td>

              <td v-if="!hideProfit" class="text-right font-weight-bold">
                {{ totalProfit | currency }}
              </td>
            </tr>
          </template>

          <template v-slot:no-data>
            <div class="empty-state">
              No product sales found for this date range.
            </div>
          </template>
        </v-data-table>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import saleService from "@/modules/ReportView/service.js";

const InvoiceDetail = () =>
  import("@/modules/ReportView/GlobalReport/InvoiceDetail.vue");

export default {
  name: "global-product-report",

  components: {
    InvoiceDetail,
  },

  props: [
    "CategoryId",
    "GroupId",
    "groupBy",
    "reset",
    "CustomerId",
    "hideProfit",
    "hideCost",
    "start",
    "end",
    "serializedItem",
  ],

  data() {
    return {
      isLoading: true,
      Product: [],
      expanded: [],
      sortBy: "totalQty",
      headers: [
        {
          text: "",
          value: "data-table-expand",
          sortable: false,
          width: 60,
        },
        {
          text: "Name",
          align: "left",
          value: "name",
          sortable: true,
        },
        {
          text: "Qty",
          align: "center",
          value: "totalQty",
          sortable: true,
        },
        {
          text: "Sale",
          align: "right",
          value: "totalPrice",
          sortable: true,
        },
        {
          text: "Cost",
          align: "right",
          value: "totalCost",
          sortable: true,
        },
        {
          text: "Profit",
          align: "right",
          value: "totalProfit",
          sortable: true,
        },
      ],
    };
  },

  computed: {
    visibleHeaders() {
      return this.headers.filter((header) => {
        if (this.hideCost && header.value === "totalCost") return false;
        if (this.hideProfit && header.value === "totalProfit") return false;

        return true;
      });
    },

    totalProfit() {
      return this.totalPrice - this.totalCost;
    },

    totalPrice() {
      return this.Product.reduce(
        (sum, row) => sum + Number(row.totalPrice || 0),
        0
      );
    },

    totalCost() {
      return this.Product.reduce(
        (sum, row) => sum + Number(row.totalCost || 0),
        0
      );
    },

    totalQty() {
      return this.Product.reduce(
        (sum, row) => sum + Number(row.totalQty || 0),
        0
      );
    },
  },

  watch: {
    reset() {
      this.getProduct();
    },
    CustomerId() {
      this.getProduct();
    },
    serializedItem() {
      this.getProduct();
    },
    start() {
      this.getProduct();
    },
    end() {
      this.getProduct();
    },
    CategoryId() {
      this.getProduct();
    },
    groupBy() {
      this.getProduct();
    },
    GroupId() {
      this.getProduct();
    },
  },

  async created() {
    await this.getProduct();
  },

  methods: {
    percentQty(qty) {
      if (!this.totalQty) return "0.00";

      return ((Number(qty || 0) / this.totalQty) * 100).toFixed(2);
    },

    getProfit(item) {
      return Number(item.totalPrice || 0) - Number(item.totalCost || 0);
    },

    profitPercent(item) {
      const cost = Number(item.totalCost || 0);
      const profit = this.getProfit(item);

      if (!cost) return "0.00";

      return ((profit / cost) * 100).toFixed(2);
    },

    async getProduct() {
      this.isLoading = true;

      const where = {};

      if (this.start) where.start = this.start;
      if (this.end) where.end = this.end;
      if (this.CustomerId) where.CustomerId = this.CustomerId;

      const groupBy = (this.groupBy || "").toLowerCase();

      if (!groupBy || groupBy === "category") {
        if (this.CategoryId) where.CategoryId = this.CategoryId;
      } else {
        where.groupBy = groupBy;

        if (this.GroupId === 0 || this.GroupId === "0") {
          where.notSelected = true;
        } else {
          where.GroupId = this.GroupId;
        }
      }

      if (this.serializedItem !== undefined) {
        where.serializedItem = this.serializedItem;
      }

      try {
        const response = await saleService.getProduct(where);

        this.Product = response.data || [];
      } catch (error) {
        console.error("getProduct failed", error);
        this.Product = [];
      } finally {
        this.isLoading = false;
      }
    },
  },
};
</script>

<style scoped>
.product-report {
  width: 100%;
}

.report-table-card {
  border-radius: 16px;
  overflow: hidden;
}

.report-table-header {
  background: #fff;
  padding: 18px 20px;
}

.report-title {
  font-size: 18px;
  font-weight: 700;
  color: #1f2937;
}

.report-subtitle {
  font-size: 13px;
  color: #6b7280;
  margin-top: 3px;
}

.summary-box {
  min-width: 110px;
  padding: 8px 12px;
  margin-left: 8px;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  text-align: right;
  background: #f9fafb;
}

.summary-label {
  font-size: 11px;
  color: #6b7280;
  text-transform: uppercase;
}

.summary-value {
  font-size: 15px;
  font-weight: 700;
  color: #111827;
}

.summary-value.loss,
.money.loss {
  color: #b91c1c;
}

.item-name {
  font-weight: 600;
  text-transform: capitalize;
  color: #111827;
}

.money {
  font-weight: 700;
}

.percent-text {
  margin-left: 6px;
  font-size: 12px;
  color: #be185d;
}

.profit-percent {
  margin-left: 6px;
  font-size: 12px;
  color: #047857;
}

.expanded-panel {
  padding: 14px;
  background: #f3f8fc;
  border-top: 1px solid #e5e7eb;
}

.total-row {
  background: #f9fafb;
}

.empty-state {
  padding: 32px;
  text-align: center;
  color: #6b7280;
}

.product-table ::v-deep th {
  font-size: 12px;
  font-weight: 700;
  color: #374151 !important;
  background: #f9fafb;
  text-transform: uppercase;
}

.product-table ::v-deep td {
  font-size: 13px;
}

@media print {
  .report-table-card {
    border: none !important;
    border-radius: 0;
  }

  .report-table-header {
    padding: 10px 0;
  }

  .summary-box {
    border: none;
    background: #fff;
  }

  .no-print {
    display: none !important;
  }
}
</style>